import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { INSIGHTS } from "@/insights";
import NewsletterForm from "@/components/NewsletterForm";

export default function InsightDetailPage() {
  const { slug } = useParams();
  const insight = INSIGHTS.find((i) => i.slug === slug);
  if (!insight) return <Navigate to="/insights" replace />;

  const related = INSIGHTS.filter((i) => i.slug !== slug).slice(0, 3);

  return (
    <div data-testid="insight-detail" className="mx-auto max-w-[900px] px-6 lg:px-10 py-14">
      <Link
        to="/insights"
        data-testid="back-to-insights"
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-8"
      >
        <ArrowLeft className="h-4 w-4" /> All insights
      </Link>

      <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-slate-500 mb-4">
        <span>{insight.category}</span>
        <span className="h-1 w-1 rounded-full bg-slate-300" />
        <span>{insight.date}</span>
        <span className="h-1 w-1 rounded-full bg-slate-300" />
        <span>{insight.read}</span>
      </div>

      <h1 className="text-3xl lg:text-5xl tracking-[-0.03em] font-semibold text-slate-900 leading-[1.05]">
        {insight.title}
      </h1>

      <div className="mt-4 text-slate-500 text-sm">By Vivek Kashyap · Founder, Indusynq</div>

      <div className="hairline my-10" />

      <article className="prose prose-slate max-w-none">
        {insight.body.map((p, i) => (
          <p key={i} className="text-[17px] text-slate-700 leading-[1.8] mb-6">
            {i === 0 && (
              <span className="float-left mr-3 mt-1 text-6xl font-semibold text-[#712ae2] leading-none">
                {p.charAt(0)}
              </span>
            )}
            {i === 0 ? p.slice(1) : p}
          </p>
        ))}
      </article>

      <div className="hairline my-12" />

      {/* Newsletter subscribe */}
      <NewsletterForm source={`insight:${slug}`} />

      <section className="mt-10 rounded-2xl bg-slate-50 border border-slate-200 p-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500">Put it to work</div>
          <h3 className="mt-2 text-xl font-semibold text-slate-900">Run this against your baseline in five minutes.</h3>
        </div>
        <Link to="/diagnostic" data-testid="detail-cta-diagnostic" className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-sm font-semibold px-5 py-2.5">
          Constraint Diagnostic <ArrowUpRight className="h-4 w-4" />
        </Link>
      </section>

      <section className="mt-14">
        <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500 mb-4">Related reading</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {related.map((r) => (
            <Link
              key={r.slug}
              to={`/insights/${r.slug}`}
              data-testid={`related-${r.slug}`}
              className="ind-card p-5 hover:-translate-y-0.5 transition-transform"
            >
              <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500">{r.category}</div>
              <div className="mt-2 text-base font-semibold text-slate-900 leading-snug">{r.title}</div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
